import { http } from 'utils'
import {pick, concat, findIndex, set} from 'lodash'

// Booking mutation imports.
import {
  SET_BOOKING, SET_BOOKINGS, SET_BOOKINGS_PAGINATION, ADD_BOOKINGS, REPLACE_BOOKING_TALENT, SET_BOOKING_PROPERTY,
  SET_FILTER, RESET_FILTERS, SET_BOOKING_HISTORY, REMOVE_BOOKING_TALENT, ADD_MESSAGE_BOOKING, BOOKING_THREAD_UNREAD_COUNT,
  SET_PAYMENT
} from 'vx/mutations'

// Booking initial state.
import initialBookingState from 'vx/state/booking'
import initialFiltersState from 'vx/state/booking/filters'

// Booking modules.
import request from './booking/request'
import note from './booking/note'
import source from './booking/source'
import file from './booking/file'

const modules = {
  request,
  note,
  file,
  source
}

// Booking state.
const state = {
  filters: initialFiltersState(),
  booking: initialBookingState(),
  bookings: [],
  pagination: {},
  history: []
}

// Booking state getters.
const getters = {
  /**
   * Initial booking filters state.
   *
   * @return object
   */
  initialFiltersState () {
    return initialFiltersState()
  },

  /**
   * Determine if we have a single booking.
   *
   * @return bool
   */
  haveBooking: (state) => {
    return state.booking !== null &&
      state.booking.id !== null
  },

  /**
   * Determine if we have no more bookings to load.
   *
   * @return bool
   */
  haveNoMoreBookings: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  }
}

// Booking actions.
const actions = {

  /**
   * Add a message to a thread list item on a booking single
   *
   * @param commit
   * @param payload
   */
  addMessageBooking ({ commit }, { payload, userId }) {
    commit(ADD_MESSAGE_BOOKING, { payload: payload, userId: userId })
  },

  /**
   * Update a threads unread count on a thread list item on a booking single
   *
   * @param commit
   * @param threadId
   * @param count
   */
  bookingThreadUnreadCount ({ commit }, { threadId, count }) {
    commit(BOOKING_THREAD_UNREAD_COUNT, { threadId: threadId, count: count })
  },

  /**
   * Get a single booking.
   *
   * @return Promise
   */
  getBooking ({ commit }, { id }) {
    return http.get(`bookings/${id}`)
      .then((response) => {
        commit(SET_BOOKING, response.data)

        return response
      })
  },

  getBookingForDuplicate ({ commit }, { id }) {
    return http.get(`bookings/${id}`)
      .then((response) => {
        response.data.files = []
        response.data.can_upload_files = 1

        if (response.data.talent) {
          response.data.talent = response.data.talent.map(item => {
            item.response.shortlisted = true
            return item
          })
        }
        commit(SET_BOOKING, response.data)

        return response
      })
  },

  resetBooking ({ commit }) {
    commit(SET_BOOKING, initialBookingState())
  },
  /**
   * Get bookings.
   *
   * @return Promise
   */
  getBookings ({ commit }, params = {}) {
    return http.get('bookings', { params })
      .then((response) => {
        commit(SET_BOOKINGS, response.data.data)
        commit(SET_BOOKINGS_PAGINATION, response.data.meta)
      })
  },

  /**
   * Get bookings.
   *
   * @return Promise
   */
  getTalentBookings ({ commit }, { id, params }) {
    return http.get(`bookings/${id}/talent_bookings`, { params })
      .then((response) => {
        commit(SET_BOOKINGS, response.data.data)
        commit(SET_BOOKINGS_PAGINATION, response.data.meta)
      })
  },

  /**
   * Get next set of paginated bookings.
   *
   * @return Promise
   */
  getNextBookings ({ commit, state, getters }, params = {}) {
    if (getters.haveNoMoreBookings) {
      return Promise.reject(new Error('There are no more bookings to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`bookings`, { params })
      .then((response) => {
        commit(ADD_BOOKINGS, response.data.data)
        commit(SET_BOOKINGS_PAGINATION, response.data.meta)

        return response
      })
  },

  /**
   * Get next set of paginated bookings.
   *
   * @return Promise
   */
  getNextTalentBookings ({ commit, state, getters }, { id, params }) {
    if (getters.haveNoMoreBookings) {
      return Promise.reject(new Error('There are no more bookings to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`bookings/${id}/talent_bookings`, { params })
      .then((response) => {
        commit(ADD_BOOKINGS, response.data.data)
        commit(SET_BOOKINGS_PAGINATION, response.data.meta)

        return response
      })
  },

  /**
   * Confirm the attendance of a talent.
   *
   * @return Promise
   */
  confirmTalentAttendance ({ commit }, { bookingId, talentId, type }) {
    return http.put(`bookings/${bookingId}/confirm_attendance/${talentId}`, {type: type})
  },

  /**
   * Mark a booking as completed.
   *
   * @return Promise
   */
  markBookingComplete ({ dispatch }, payload) {
    const url = payload.userId ? `bookings/${payload.id}/mark_complete/${payload.userId}` : `bookings/${payload.id}/mark_complete`

    return http.put(url, payload)
      .then((response) => {
        dispatch('setBookingProperty', { property: 'status', value: response.data.status })
        return response
      })
  },

  /**
   * Accept a bookings terms and conditions.
   *
   * @return Promise
   */
  acceptTermsAndConditions ({ commit }, { id, payload }) {
    return http.put(`bookings/${id}/accept_terms`, payload)
  },

  /**
   * Decline a bookings terms and conditions.
   *
   * @return Promise
   */
  declineTermsAndConditions ({ commit }, { id, payload }) {
    return http.put(`bookings/${id}/decline_terms`, payload)
  },

  /**
   * Update a booking.
   *
   * @return Promise
   */
  updateBooking ({ commit }, { id, payload }) {
    return http.put(`bookings/${id}`, payload)
      .then(response => {
        commit(SET_BOOKING, response.data)
      })
  },

  /**
   * Update a bookings admin options.
   *
   * @return Promise
   */
  updateBookingAdminOptions ({ dispatch }, { id, commissionRate, commissionDollar, commissionType, talentExpire, talentCanComment, marketSegment, adminOpportunityId, bookingExpires, expiryDate, canUploadFiles, regionId }) {
    return http.put(`bookings/${id}/update_admin_options`, {
      commission_rate: commissionRate,
      commission_dollar: commissionDollar,
      commission_type: commissionType,
      talent_expire: talentExpire,
      admin_opportunity_id: adminOpportunityId,
      talent_can_comment: talentCanComment,
      market_segment: marketSegment,
      expires: bookingExpires,
      billboard_expiry_date: expiryDate,
      can_upload_files: canUploadFiles,
      region_id: regionId
    }).then((response) => {
      dispatch('setBookingProperty', { property: 'budget_per_star_as_user_rate', value: response.data.budget_per_star_as_user_rate })
      dispatch('setBookingProperty', { property: 'talent_expire', value: response.talent_expire })
      dispatch('setBookingProperty', { property: 'talent_can_comment', value: response.talent_can_comment })
      dispatch('setBookingProperty', { property: 'can_upload_files', value: response.can_upload_files })

      if (response.market_segment) {
        dispatch('setBookingProperty', { property: 'market_segment', value: response.market_segment })
      } else {
        dispatch('setBookingProperty', { property: 'market_segment', value: response.data.market_segment })
      }

      if (response.market_segment) {
        dispatch('setBookingProperty', { property: 'admin_opportunity_id', value: response.admin_opportunity_id })
      } else {
        dispatch('setBookingProperty', { property: 'admin_opportunity_id', value: response.data.admin_opportunity_id })
      }

      return response
    })
  },

  /**
   * Cancel a booking.
   *
   * @return Promise
   */
  cancelBooking ({ commit }, { id, reason }) {
    return http.delete(`bookings/${id}`, {params: { reason }})
      .then((response) => {
        commit(SET_BOOKING, response.data)

        return response
      })
  },

  /**
   * Cancel a booking.
   *
   * @return Promise
   */
  refundBooking ({ commit }, { id }) {
    return http.delete(`packages/${id}/refund`)
      .then((response) => {
        commit(SET_BOOKING, response.data)

        return response
      })
  },

  /**
   *
   */
  notifyAdminOfTalentAttemptToCancel ({ commit }, { id }) {
    return http.post(`bookings/${id}/notify_attempt_to_cancel`)
  },

  /**
   *
   */
  notifyTalentPaymentSent ({ commit }, { bookingId, talentId }) {
    return http.post(`bookings/${bookingId}/user/${talentId}/notify_talent_paid`)
      .then((response) => {
        commit(REPLACE_BOOKING_TALENT, {id: talentId, talent: response.data})

        return response
      })
  },

  /**
   * Mark a booking as booked but unpaid
   *
   * @return Promise
   */
  createBookingThreads ({ commit }, { id }) {
    return http.post(`bookings/${id}/create_threads`)
      .then((response) => {
        commit(SET_BOOKING, response.data)

        return response
      })
  },

  /**
   * Mark a booking as booked but unpaid
   *
   * @return Promise
   */
  markBookingBookedButUnpaid ({ commit }, { id }) {
    return http.post(`bookings/${id}/mark_booked_unpaid`)
      .then((response) => {
        commit(SET_BOOKING, response.data)

        return response
      })
  },

  markBookingIncomplete ({ commit }, { id }) {
    return http.put(`bookings/${id}/mark_incomplete`)
      .then((response) => {
        commit(SET_BOOKING, response.data)

        return response
      })
  },

  /**
   * Reactivate a booking
   *
   * @return Promise
   */
  reactivateBooking ({ commit }, { id }) {
    return http.get(`bookings/${id}/restore`)
      .then((response) => {
        commit(SET_BOOKING, response.data)

        return response
      })
  },

  /**
   * Restore a booking from being "on hold".
   *
   * @return Promise
   */
  restoreBookingOnHold ({ commit }, { id }) {
    return http.put(`bookings/${id}/restore_from_hold`)
      .then((response) => {
        commit(SET_BOOKING, response.data)

        return response
      })
  },

  /**
   * Replace talent on a booking.
   *
   * @return Promise
   */
  replaceTalent ({ commit }, { id, payload }) {
    return http.put(`bookings/${id}/replace_talent`, payload)
      .then((response) => {
        commit(SET_BOOKING, response.data)

        return response
      })
  },

  getHistory ({ commit }, { id }) {
    return http.get(`bookings/${id}/history`)
      .then((response) => {
        commit(SET_BOOKING_HISTORY, response.data)

        return response
      })
  },

  getHistoryLegacy ({ commit }, { id }) {
    return http.get(`bookings/${id}/history_legacy`)
      .then((response) => {
        commit(SET_BOOKING_HISTORY, response.data)

        return response
      })
  },

  /**
   * Get a booking PDF.
   * The audience and userId are for when an admin is retrieving pdf acting as the audience.
   *
   * @return Promise
   */
  getPdf (store, { id, role }) {
    return http.get(`bookings/${id}/pdf`, {params: {role: role}})
  },

  /**
   * Duplicate a booking request.
   */
  duplicateBooking ({ commit }, { id, payload }) {
    return http.post(`bookings/${id}/duplicate`, payload)
  },

  /**
   * Remove booking shortlist talent.
   */
  removeBookingShortlistedTalent ({ commit }, id) {
    commit(REMOVE_BOOKING_TALENT, { id })
  },

  /**
   * Set the booking.
   */
  setBooking ({ commit }, booking) {
    commit(SET_BOOKING, booking)
  },

  setBookingProperty ({ commit }, { property, value }) {
    commit(SET_BOOKING_PROPERTY, { property, value })
  },

  replaceBookingTalent ({ commit }, { id, talent }) {
    commit(REPLACE_BOOKING_TALENT, { id, talent })
  },

  setFilter ({ commit }, { filter, value }) {
    commit(SET_FILTER, { filter, value })
  },

  resetFilters ({ commit }) {
    commit(RESET_FILTERS)
  },

  assignAdmin ({ commit }, { bookingId, userId }) {
    return http.put(`bookings/${bookingId}/update_admin_assigned`, { userId })
  },

  revenueCategory ({ commit }, { bookingId, revenueCategory }) {
    return http.put(`bookings/${bookingId}/update_revenue_category`, { revenueCategory })
  },

  updatePayment ({ commit }, { id, payload }) {
    return http.post(`payments/${id}`, payload)
      .then((response) => {
        commit(SET_PAYMENT, response.data)

        return response
      })
  },

  markPaymentUnpaid ({ commit }, { transactionId }) {
    return http.put(`payments/${transactionId}/payment_unpaid`)
      .then((response) => {
        commit(SET_PAYMENT, response.data[0])
        commit(SET_PAYMENT, response.data[1])

        return response
      })
  }
}

// Booking state mutations.
const mutations = {
  [ADD_MESSAGE_BOOKING] (state, { payload, userId }) {
    let index = findIndex(state.booking.threads, current => current.id === payload.message.thread_id)
    if (index !== -1) {
      state.booking.threads[index].latest_message = payload.message
      if (payload.message.user_id !== userId) {
        state.booking.threads[index].unread_count = state.booking.threads[index].unread_count + 1
      }
    }
  },

  [BOOKING_THREAD_UNREAD_COUNT] (state, { threadId, count }) {
    let index = findIndex(state.booking.threads, current => current.id === threadId)
    if (index !== -1) {
      state.booking.threads[index].unread_count = count < 0 ? 0 : count
    }
  },

  [SET_BOOKING] (state, booking) {
    state.booking = booking
  },

  [SET_BOOKINGS] (state, bookings) {
    state.bookings = bookings
  },

  [SET_BOOKINGS_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [ADD_BOOKINGS] (state, bookings) {
    state.bookings = concat(state.bookings, bookings)
  },

  [SET_BOOKING_PROPERTY] (state, { property, value }) {
    set(state.booking, property, value)
  },

  [REPLACE_BOOKING_TALENT] (state, { id, talent }) {
    let index = findIndex(state.booking.talent, existing => existing.id === id)

    if (index !== undefined) {
      state.booking.talent.splice(index, 1, talent)
    }
  },

  [REMOVE_BOOKING_TALENT] (state, { id }) {
    let index = findIndex(state.booking.talent, talent => talent.id === id)

    state.booking.talent.splice(index, 1)
  },

  [SET_FILTER] (state, { filter, value }) {
    set(state.filters, filter, value)
  },

  [RESET_FILTERS] (state) {
    state.filters = initialFiltersState()
  },

  [SET_BOOKING_HISTORY] (state, history) {
    state.history = history
  },

  [SET_PAYMENT] (state, payment) {
    let index = findIndex(state.booking.payments, p => p.id === payment.id)

    if (index !== -1) {
      state.booking.payments.splice(index, 1, payment)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, modules, state, getters, actions, mutations
}
